// @ts-check
// Seção "Filtros do widget" do editor (contrato 07, seção 8.3): período, unidade, consultor e período letivo próprios
// do widget, que valem no lugar dos filtros do painel. Vazio = segue o painel.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findManyRecords } from '../../data/rest.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { formatFullName, getRecordLabel } from '../../lib/field-metadata.js';
import { WIDGET_FILTER_PERIODS } from '../../../../shared/dashboards/widget-filters.js';
import { EditorField, EditorSection, SelectControl } from './editor-controls.js';
import { t } from '../../i18n/index.js';

/** @type {Map<string, Promise<{ value: string, label: string }[]>>} */
const optionsCache = new Map();

/**
 * Registros de uma relação do filtro (unidades e períodos letivos são poucos: até 200).
 * @param {string} objectNameSingular
 */
function useRecordOptions(objectNameSingular) {
  const [options, setOptions] = useState(/** @type {{ value: string, label: string }[]} */ ([]));
  useEffect(() => {
    const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
    if (!objectMetadata) return undefined;
    let isCancelled = false;
    if (!optionsCache.has(objectNameSingular)) {
      const promise = findManyRecords(objectMetadata, { limit: 200, depth: 0 })
        .then(({ records }) => records.map((record) => ({ value: record.id, label: getRecordLabel(objectMetadata, record) || record.id })));
      promise.catch(() => optionsCache.delete(objectNameSingular));
      optionsCache.set(objectNameSingular, promise);
    }
    optionsCache.get(objectNameSingular)?.then((loaded) => {
      if (!isCancelled) setOptions(loaded);
    }).catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [objectNameSingular]);
  return options;
}

/**
 * @param {{ value: Record<string, any> | undefined, onChange: (value: Record<string, any> | undefined) => void }} props
 */
export function WidgetFiltersSection({ value, onChange }) {
  const filters = value ?? {};
  const campusOptions = useRecordOptions('campus');
  const academicPeriodOptions = useRecordOptions('academicPeriod');
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  const memberOptions = (workspaceMembersState.value ?? []).map((member) => ({
    value: member.id, label: formatFullName(member.name) || member.userEmail || member.id, icon: 'IconUserCircle',
  }));
  const set = (/** @type {string} */ key, /** @type {string | null} */ next) => {
    const updated = { ...filters, [key]: next || undefined };
    const compact = Object.fromEntries(Object.entries(updated).filter(([, entry]) => entry !== undefined && entry !== null && entry !== ''));
    onChange(Object.keys(compact).length > 0 ? compact : undefined);
  };
  const followsDashboard = t('dashboardParity.widgetFilters.followDashboard');
  return html`<${EditorSection} title=${t('dashboardParity.widgetFilters.section')}>
    <p class="widget-editor-hint">${t('dashboardParity.widgetFilters.hint')}</p>
    <${EditorField} label=${t('dashboards.filters.period')} icon="IconCalendar">
      <${SelectControl} value=${filters.period} allowEmpty=${true} emptyLabel=${followsDashboard}
        options=${WIDGET_FILTER_PERIODS.map((period) => ({ value: period, label: t(`dashboardParity.widgetFilters.periods.${period}`) }))}
        onChange=${(period) => set('period', period)} />
    <//>
    ${objectsByNameSingular.value.has('campus')
      ? html`<${EditorField} label=${t('dashboards.filters.campus')} icon="IconBuildingCommunity">
          <${SelectControl} value=${filters.campusId} allowEmpty=${true} emptyLabel=${followsDashboard} searchable=${true}
            options=${campusOptions} onChange=${(campusId) => set('campusId', campusId)} />
        <//>`
      : null}
    <${EditorField} label=${t('dashboards.filters.consultant')} icon="IconUserCircle">
      <${SelectControl} value=${filters.ownerWorkspaceMemberId} allowEmpty=${true} emptyLabel=${followsDashboard} searchable=${true}
        options=${memberOptions} onChange=${(memberId) => set('ownerWorkspaceMemberId', memberId)} />
    <//>
    ${objectsByNameSingular.value.has('academicPeriod')
      ? html`<${EditorField} label=${t('dashboards.filters.academicPeriod')} icon="IconCalendarStats">
          <${SelectControl} value=${filters.academicPeriodId} allowEmpty=${true} emptyLabel=${followsDashboard} searchable=${true}
            options=${academicPeriodOptions} onChange=${(academicPeriodId) => set('academicPeriodId', academicPeriodId)} />
        <//>`
      : null}
  <//>`;
}
